import {Injectable} from '@angular/core';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {isEmbeddedInIframe, isMPay, isNestBank, isSkycash} from '../../core/utils/utils';
import {ApplicationStateService} from './application-state.service';
import {MetricsService} from './metrics.service';
import {CustomMetricsName} from '../components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../components/models/metrics/metrics-type.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {BusinessParamName} from '../models/request-offer/request-offer.enum';
import {QueryParamName} from '../../core/enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class ApplicationEventsService {

  offerPayEventDebugInfo;

  get isMobileOrigin(): boolean {
    return this.urlParamsService.isMobileOrigin;
  }

  get isMobileOriginOrEmailDefined(): boolean {
    return this.urlParamsService.isMobileOrigin || this.urlParamsService.isEmailDefined;
  }

  get isMobileOriginOrEmbeddedInIframe(): boolean {
    return this.urlParamsService.isMobileOrigin || isEmbeddedInIframe();
  }

  get partnerExternalId(): string | null {
    const partnerExternalIdUrl = this.urlParamsService.getQueryParam(QueryParamName.PARTNER_EXTERNAL_ID);
    if (isNotNullOrUndefined(partnerExternalIdUrl)) {
      return partnerExternalIdUrl;
    }
    return <string | null>this.stateService.getBusinessParameterValue(BusinessParamName.PARTNER_EXTERNAL_ID);
  }

  private readonly APPLICATION_EVENTS_CONFIG: ApplicationEventConfig[] = [
    {
      event: ApplicationEvent.INIT_SESSION,
      canSend: () => (isMPay() && this.isMobileOrigin) || (isSkycash() && this.isMobileOriginOrEmailDefined)
    },
    {
      event: ApplicationEvent.CALCULATE,
      canSend: () => (isMPay() && this.isMobileOrigin) || (isSkycash() && this.isMobileOriginOrEmailDefined)
    },
    {
      event: ApplicationEvent.PREPARE_REGISTRATION,
      canSend: () => (isMPay() && this.isMobileOrigin) || (isSkycash() && this.isMobileOriginOrEmailDefined)
    },
    {
      event: ApplicationEvent.OFFER_PAY,
      canSend: () => (isMPay() && this.isMobileOrigin) || (isSkycash() && this.isMobileOriginOrEmailDefined) || (isNestBank() && this.isMobileOriginOrEmbeddedInIframe)
    }
  ];

  constructor(private urlParamsService: UrlParamsService,
              private metricsService: MetricsService,
              private stateService: ApplicationStateService) { }

  sendEvent(event: ApplicationEvent, payload?: any) {
    switch (event) {
      case ApplicationEvent.INIT_SESSION:
        this.sendInitSessionEvent();
        break;
      case ApplicationEvent.CALCULATE:
        this.sendCalculateEvent(payload);
        break;
      case ApplicationEvent.PREPARE_REGISTRATION:
        this.sendPrepareRegistrationEvent(payload);
        break;
      case ApplicationEvent.OFFER_PAY:
        this.sendOfferPayEvent(payload);
        break;
    }
  }

  private getConfig(event: ApplicationEvent): ApplicationEventConfig {
    return this.APPLICATION_EVENTS_CONFIG.find(config => config.event === event);
  }

  private sendInitSessionEvent(): void {
    if (this.getConfig(ApplicationEvent.INIT_SESSION).canSend()) {
      const eventPayload = {
        sessionId: this.stateService.sessionId,
        partnerExternalId: this.partnerExternalId
      };
      this.sendApplicationEvent(ApplicationEvent.INIT_SESSION, eventPayload);
    }
  }

  private sendCalculateEvent(payload: any): void {
    if (this.getConfig(ApplicationEvent.CALCULATE).canSend()) {
      const eventPayload = {
        sessionId: this.stateService.sessionId,
        partnerExternalId: this.partnerExternalId,
        ...payload
      };
      this.sendApplicationEvent(ApplicationEvent.CALCULATE, eventPayload);
    }
  }

  private sendPrepareRegistrationEvent(payload: any): void {
    if (this.getConfig(ApplicationEvent.PREPARE_REGISTRATION).canSend()) {
      const eventPayload = {
        sessionId: this.stateService.sessionId,
        partnerExternalId: this.partnerExternalId,
        ...payload
      };
      this.sendApplicationEvent(ApplicationEvent.PREPARE_REGISTRATION, eventPayload);
    }
  }

  private sendOfferPayEvent(payload: any): void {
    if (this.getConfig(ApplicationEvent.OFFER_PAY).canSend()) {
      let eventPayload: any;
      if (isNestBank()) {
        eventPayload = {
          ...payload
        };
      } else {
        eventPayload = {
          sessionId: this.stateService.sessionId,
          partnerExternalId: this.partnerExternalId,
          ...payload
        };
      }
      const eventType = isNestBank() ? ApplicationEvent.OFFER_PAY_NEXT : ApplicationEvent.OFFER_PAY;
      this.sendApplicationEvent(eventType, eventPayload);
    }
  }

  private sendApplicationEvent(eventType: ApplicationEvent, payload: any): void {
    let packet: any = isNestBank() ? {
      eventName: eventType,
      payload: payload
    } : {
      type: eventType,
      payload: payload
    };
    if (isSkycash() || isMPay()) {
      packet = JSON.stringify(packet);
    }

    if (this.urlParamsService.isTestMode) {
      console.log('SENDING APPLICATION EVENT\n', JSON.stringify(packet, null, '\t'));
    }

    try { // Android
      if (eventType === ApplicationEvent.OFFER_PAY) {
        this.openOfferPayEventDialog(packet, 'appBridge');
      }
      // @ts-ignore
      appBridge.postMessage(packet);
      this.sendApplicationEventMetric(eventType, ApplicationEventDestination.ANDROID, packet);
      return;
    } catch (e) {}

    try { // iOS
      if (eventType === ApplicationEvent.OFFER_PAY) {
        this.openOfferPayEventDialog(packet, 'window.webkit.messageHandlers.appBridge');
      }
      window['webkit']['messageHandlers']['appBridge'].postMessage(packet);
      this.sendApplicationEventMetric(eventType, ApplicationEventDestination.IOS, packet);
      return;
    } catch (e) {}

    if (eventType === ApplicationEvent.OFFER_PAY) {
      this.openOfferPayEventDialog(packet, 'window');
    }
    // standard post message
    window.postMessage(packet, '*');

    if (isNotNullOrUndefined(window.parent)) {
      window.parent.postMessage(packet, '*');
    }

    this.sendApplicationEventMetric(eventType, ApplicationEventDestination.OTHER, packet);
  }

  private openOfferPayEventDialog(payload: any, postMessageObject: string): void {
    this.offerPayEventDebugInfo = {
      postMessageObject: postMessageObject,
      payload: payload
    }
  }

  private sendApplicationEventMetric(eventName: ApplicationEvent, destination: ApplicationEventDestination, body: any): void {
    this.metricsService.sendSimpleMetric(CustomMetricsName.APPLICATION_EVENT, MetricsType.PROCESS, JSON.stringify({
      eventName: eventName,
      destination: destination,
      body: body
    }))
  }
}

export enum ApplicationEvent {
  INIT_SESSION = 'init-session',
  CALCULATE = 'calculate',
  PREPARE_REGISTRATION = 'prepare-registration',
  OFFER_PAY = 'offer-pay',
  OFFER_PAY_NEXT = 'OFFER_PAY', // NestBank
}

export enum ApplicationEventDestination {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
  OTHER = 'OTHER',
}

interface ApplicationEventConfig {
  event: ApplicationEvent;
  canSend: () => boolean;
}
